import { useState } from 'react';
import {
  UMButton, UMFormGroup, UMFormStyles, UMMessage, UMSectionTitle, UMSidePanel, UMStack,
} from '../_components';
import { ProdControlStyles, ProdSelect } from './ProdControls';
import { ExportCheckboxTree } from './ExportCheckboxTree';
import { BUILDING_DEFAULT_CHECKED, BUILDING_TREE, EXPANDED_LAYER_GROUPS, OTHER_LAYERS_TREE, OTHER_LAYER_IDS } from './exportTree';
import { PerimeterField } from './PerimeterField';
import type { ExportFormat, ExportPerimeter, ExportRequest } from './types';

/**
 * Variant of UMExportPanel (_components/UMRailPanels.tsx): same frame, sections and footer,
 * plus an "Autres couches" tree (GeoJSON only) mirroring the Couches menu organisation.
 */
export interface ExportPanelGeojsonProps {
  onClose: () => void;
  onHistory?: () => void;
  perimeter: ExportPerimeter | null;
  onPerimeterChange: (perimeter: ExportPerimeter | null) => void;
  onExport: (request: ExportRequest) => void;
  pending?: boolean;
  error?: string | null;
  defaultFormat?: ExportFormat;
}

/** Groupes de couches repliés à l'ouverture (tous sauf ceux dépliés dans la maquette). */
const COLLAPSED_GROUPS = (OTHER_LAYERS_TREE.children ?? [])
  .filter((g) => !EXPANDED_LAYER_GROUPS.some((id) => g.id === `group:${id}`))
  .map((g) => g.id);

export function ExportPanelGeojson({
  onClose, onHistory, perimeter, onPerimeterChange, onExport, pending = false, error, defaultFormat = 'geojson',
}: ExportPanelGeojsonProps) {
  const [format, setFormat] = useState<ExportFormat>(defaultFormat);
  const [buildingData, setBuildingData] = useState<string[]>(BUILDING_DEFAULT_CHECKED);
  const [buildingsSelected, setBuildingsSelected] = useState(true);
  const [layers, setLayers] = useState<string[]>([]);
  const isGeojson = format === 'geojson';
  const exportedBuildingData = buildingsSelected ? buildingData : [];
  const exportedLayers = isGeojson ? layers.filter((id) => OTHER_LAYER_IDS.includes(id)) : [];
  const nothingToExport = !buildingsSelected && exportedLayers.length === 0;

  const submit = () => {
    if (!perimeter || nothingToExport) return;
    onExport({ format, perimeterId: perimeter.id, perimeterLabel: perimeter.label, buildingData: exportedBuildingData, layers: exportedLayers });
  };

  return (
    <UMSidePanel
      title="Export des données"
      onClose={onClose}
      onHistory={onHistory}
      footer={
        <>
          <UMButton variant="secondary" onClick={onClose}>Fermer</UMButton>
          <UMButton disabled={!perimeter || pending || nothingToExport} onClick={submit}>{pending ? 'Export en cours…' : 'Exporter'}</UMButton>
        </>
      }
    >
      <UMFormStyles />
      <ProdControlStyles />
      <UMStack gap={16}>
        <UMStack>
          <UMSectionTitle>Contenu de l'export</UMSectionTitle>
          <UMFormGroup label="Périmètre">
            <PerimeterField value={perimeter} onChange={onPerimeterChange} />
          </UMFormGroup>
        </UMStack>
        <UMStack>
          <UMSectionTitle>Format et Données</UMSectionTitle>
          <UMFormGroup label="Format">
            <ProdSelect
              ariaLabel="Format"
              value={format}
              onChange={(v) => setFormat(v as ExportFormat)}
              options={[{ value: 'xlsx', label: 'Excel' }, { value: 'geojson', label: 'GeoJSON' }]}
            />
          </UMFormGroup>
          <UMFormGroup label="Données à exporter">
            <ExportCheckboxTree
              nodes={BUILDING_TREE}
              checked={buildingData}
              onChange={setBuildingData}
              rootEnabled={buildingsSelected}
              onRootEnabledChange={setBuildingsSelected}
            />
            {isGeojson && (
              <ExportCheckboxTree
                nodes={[OTHER_LAYERS_TREE]}
                checked={layers}
                onChange={setLayers}
                defaultCollapsed={COLLAPSED_GROUPS}
              />
            )}
          </UMFormGroup>
          {isGeojson && (
            <div style={{ fontSize: 12, lineHeight: '16px' }}>
              {nothingToExport
                ? 'Aucune couche sélectionnée.'
                : exportedLayers.length === 0
                  ? 'Seule la couche Bâtiments sera exportée.'
                  : buildingsSelected
                    ? `${exportedLayers.length + 1} couches exportées (Bâtiments + ${exportedLayers.length}), une couche GeoJSON par fichier.`
                    : `${exportedLayers.length} couches exportées (sans Bâtiments), une couche GeoJSON par fichier.`}
            </div>
          )}
          {error && <UMMessage title="Échec de l'export">{error}</UMMessage>}
        </UMStack>
      </UMStack>
    </UMSidePanel>
  );
}
